import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Set this to your reCAPTCHA Enterprise site key to turn on App Check bot
// mitigation. Leave empty to keep App Check off (the function's enforceAppCheck
// must stay false while this is empty). See DEPLOY.md.
const APP_CHECK_SITE_KEY = "";

const firebaseConfig = {
  apiKey: "AIzaSyAkmrdI2ndhOM6Y-jJwzqYYFBwl9zDjZAk",
  authDomain: "deadsignal-prod-rdsg.firebaseapp.com",
  projectId: "deadsignal-prod-rdsg",
  storageBucket: "deadsignal-prod-rdsg.firebasestorage.app",
  messagingSenderId: "40001652249",
  appId: "1:40001652249:web:f68d1c1d8a547819868c88"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Initialize Cloud Functions
export const functions = getFunctions(app);

// Initialize Auth
export const auth = getAuth(app);

// Initialize App Check when a site key is configured. Bots and scripted abuse
// of the public proxy are rejected before reaching Gemini; real browsers are
// unaffected and recruiters see nothing.
if (typeof window !== "undefined" && APP_CHECK_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// Connect to the local Functions emulator ONLY when explicitly opted in, via
// VITE_USE_EMULATOR=true at build time or an `?emulator` query param at runtime.
// By default, local dev (localhost) calls the live deployed backend so the demo
// exercises real Gemini instead of the deterministic offline fallback. Routing
// every generateProxy call to a (usually not-running) emulator on :5001 just
// because the host was "localhost" made local runs silently mock the model.
const useEmulator =
  typeof window !== "undefined" &&
  (import.meta.env?.VITE_USE_EMULATOR === "true" ||
    new URLSearchParams(window.location.search).has("emulator"));
if (useEmulator) {
  connectFunctionsEmulator(functions, "localhost", 5001);
}

/**
 * Resolve the URL of an onRequest (HTTP) Cloud Function, matching wherever the
 * callable SDK is currently pointed: the local emulator when emulator mode is
 * opted in, otherwise the deployed us-central1 endpoint. Used by the streaming
 * dialogue path (generateStream).
 */
export function getFunctionUrl(name) {
  if (useEmulator) {
    return `http://localhost:5001/${firebaseConfig.projectId}/us-central1/${name}`;
  }
  return `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/${name}`;
}

/**
 * authReady resolves once an anonymous session exists.
 *
 * The callable Cloud Function generateProxy rejects any request without
 * request.auth, so the client must hold a signed-in session before the first
 * LLM call; otherwise vertexClient.js falls back to deterministic mock text and
 * the demo only appears to use live AI. Anonymous sign-in satisfies the auth
 * gate without asking the player for credentials. Pair this with a per-session
 * usage cap and Firebase App Check if abuse of the public proxy becomes a
 * billing concern.
 */
export const authReady = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      unsubscribe();
      resolve(user);
    }
  });
  signInAnonymously(auth).catch((err) => {
    console.error("Anonymous sign-in failed:", err);
    resolve(null);
  });
});

export default app;
